"use client"

import { useState } from "react"
import { HexColorPicker } from "react-colorful"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  RibbonDropdownTrigger,
  RibbonGroup,
  RibbonIconButton,
  RibbonToolbar,
} from "@/components/editor/ribbon/ribbon-primitives"
import { useRibbonActions } from "@/components/editor/ribbon/use-ribbon-actions"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const FONT_FAMILIES = [
  "Times New Roman",
  "Arial",
  "Calibri",
  "Georgia",
  "Courier New",
  "Helvetica",
  "Palatino Linotype",
  "Garamond",
  "Verdana",
  "Trebuchet MS",
  "Comic Sans MS",
  "TeX Gyre Termes",
  "TeX Gyre Heros",
  "Latin Modern Roman",
]

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36, 48, 72]

const R = "/ribbion_menu"

export function HomeToolbar() {
  const actions = useRibbonActions()
  const [fontFamily, setFontFamily] = useState("Times New Roman")
  const [fontSize, setFontSize] = useState(12)
  const [fontOpen, setFontOpen] = useState(false)
  const [sizeOpen, setSizeOpen] = useState(false)
  const [customSize, setCustomSize] = useState("")
  const [fillColor, setFillColor] = useState("#FDE68A")
  const [textColor, setTextColor] = useState("#111827")
  const [fillOpen, setFillOpen] = useState(false)
  const [textOpen, setTextOpen] = useState(false)

  return (
    <RibbonToolbar>
      <RibbonGroup>
        <RibbonIconButton
          icon={`${R}/cutIcon.svg`}
          hoverHint={`${R}/cutHover.svg`}
          label="Cut"
          onClick={actions.cut}
        />
        <RibbonIconButton
          icon={`${R}/copyIcon.svg`}
          hoverHint={`${R}/copyHover.svg`}
          label="Copy"
          onClick={actions.copy}
        />
        <RibbonIconButton
          icon={`${R}/pasteIcon.svg`}
          hoverHint={`${R}/pasteHover.svg`}
          label="Paste"
          onClick={actions.paste}
        />
      </RibbonGroup>

      <RibbonGroup className="gap-1 bg-transparent px-0">
        <Tooltip delayDuration={400}>
          <TooltipTrigger asChild>
            <span>
              <Popover open={fontOpen} onOpenChange={setFontOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Font family"
                    className={cn(
                      "flex h-7 w-[148px] items-center justify-between rounded-[5px] border border-[var(--ed-ribbon-group-divider)] bg-[var(--ed-ribbon-field-bg)] px-2 text-left text-xs text-[var(--ed-ribbon-field-text)]",
                      "hover:bg-[#F9FAFB]",
                    )}
                  >
                    <span className="truncate">{fontFamily}</span>
                    <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search fonts..." />
                    <CommandList>
                      <CommandEmpty>No font found.</CommandEmpty>
                      <CommandGroup>
                        {FONT_FAMILIES.map((font) => (
                          <CommandItem
                            key={font}
                            value={font}
                            onSelect={() => {
                              setFontFamily(font)
                              setFontOpen(false)
                              actions.fontFamily(font)
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-3.5 w-3.5",
                                fontFamily === font ? "opacity-100" : "opacity-0",
                              )}
                            />
                            <span style={{ fontFamily: font }}>{font}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            sideOffset={6}
            hideArrow
            className="border-0 bg-transparent p-0 shadow-none"
          >
            <img
              src={`${R}/fontFamilyHover.svg`}
              alt="Font Family"
              draggable={false}
              className="max-w-none drop-shadow-lg"
            />
          </TooltipContent>
        </Tooltip>

        <Tooltip delayDuration={400}>
          <TooltipTrigger asChild>
            <span>
              <Popover open={sizeOpen} onOpenChange={setSizeOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Font size"
                    className={cn(
                      "flex h-7 w-[52px] items-center justify-between rounded-[5px] border border-[var(--ed-ribbon-group-divider)] bg-[var(--ed-ribbon-field-bg)] px-1.5 text-xs text-[var(--ed-ribbon-field-text)]",
                      "hover:bg-[#F9FAFB]",
                    )}
                  >
                    <span>{fontSize}</span>
                    <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[140px] p-1" align="start">
                  <div className="max-h-[200px] overflow-y-auto">
                    {FONT_SIZES.map((size) => (
                      <button
                        key={size}
                        type="button"
                        className={cn(
                          "flex w-full rounded px-2 py-1 text-left text-xs hover:bg-accent",
                          fontSize === size && "bg-accent font-medium",
                        )}
                        onClick={() => {
                          setFontSize(size)
                          setSizeOpen(false)
                          actions.fontSize(size)
                        }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  <div className="mt-1 border-t pt-1">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        const n = Number(customSize)
                        if (!Number.isFinite(n) || n <= 0) return
                        setFontSize(n)
                        setSizeOpen(false)
                        actions.fontSize(n)
                      }}
                      className="flex gap-1"
                    >
                      <Input
                        value={customSize}
                        onChange={(e) => setCustomSize(e.target.value)}
                        placeholder="Custom"
                        className="h-7 text-xs"
                        type="number"
                        min={1}
                        max={200}
                      />
                      <button
                        type="submit"
                        className="h-7 rounded bg-[#2563EB] px-2 text-[10px] text-white"
                      >
                        Set
                      </button>
                    </form>
                  </div>
                </PopoverContent>
              </Popover>
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            sideOffset={6}
            hideArrow
            className="border-0 bg-transparent p-0 shadow-none"
          >
            <img
              src={`${R}/fontSizeHover.svg`}
              alt="Font Size"
              draggable={false}
              className="max-w-none drop-shadow-lg"
            />
          </TooltipContent>
        </Tooltip>
      </RibbonGroup>

      <RibbonGroup>
        <RibbonIconButton
          icon={`${R}/boldIcon.svg`}
          hoverHint={`${R}/boldHover.svg`}
          label="Bold"
          onClick={actions.bold}
        />
        <RibbonIconButton
          icon={`${R}/italicIcon.svg`}
          hoverHint={`${R}/italicHover.svg`}
          label="Italic"
          onClick={actions.italic}
        />
        <RibbonIconButton
          icon={`${R}/underlineIcon.svg`}
          hoverHint={`${R}/underlineHover.svg`}
          label="Underline"
          onClick={actions.underline}
        />
      </RibbonGroup>

      <RibbonGroup>
        <Popover open={fillOpen} onOpenChange={setFillOpen}>
          <PopoverTrigger asChild>
            <span>
              <RibbonDropdownTrigger
                icon={`${R}/fillColorIcon.svg`}
                hoverHint={`${R}/fillColorHover.svg`}
                label="Fill Color"
              />
            </span>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <HexColorPicker color={fillColor} onChange={setFillColor} />
            <button
              type="button"
              className="mt-2 w-full rounded bg-[#2563EB] py-1.5 text-xs text-white"
              onClick={() => {
                actions.fillColor(fillColor)
                setFillOpen(false)
              }}
            >
              Apply fill
            </button>
          </PopoverContent>
        </Popover>

        <Popover open={textOpen} onOpenChange={setTextOpen}>
          <PopoverTrigger asChild>
            <span>
              <RibbonDropdownTrigger
                icon={`${R}/textColorIcon.svg`}
                hoverHint={`${R}/testColorHover.svg`}
                label="Text Color"
              />
            </span>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <HexColorPicker color={textColor} onChange={setTextColor} />
            <button
              type="button"
              className="mt-2 w-full rounded bg-[#2563EB] py-1.5 text-xs text-white"
              onClick={() => {
                actions.textColor(textColor)
                setTextOpen(false)
              }}
            >
              Apply color
            </button>
          </PopoverContent>
        </Popover>
      </RibbonGroup>

      <RibbonGroup>
        <RibbonIconButton
          icon={`${R}/alignLeftIcon.svg`}
          hoverHint={`${R}/alignLeftHover.svg`}
          label="Align Left"
          onClick={actions.alignLeft}
        />
        <RibbonIconButton
          icon={`${R}/alignCenterIcon.svg`}
          hoverHint={`${R}/alignCenterHover.svg`}
          label="Align Center"
          onClick={actions.alignCenter}
        />
        <RibbonIconButton
          icon={`${R}/alignRightIcon.svg`}
          hoverHint={`${R}/alignRightHover.svg`}
          label="Align Right"
          onClick={actions.alignRight}
        />
        <RibbonIconButton
          icon={`${R}/justifyIcon.svg`}
          hoverHint={`${R}/justifyHover.svg`}
          label="Justify"
          onClick={actions.justify}
        />
      </RibbonGroup>

      <RibbonGroup>
        <RibbonIconButton
          icon={`${R}/bulletListIcon.svg`}
          hoverHint={`${R}/bulletListHover.svg`}
          label="Bullet List"
          onClick={actions.bulletList}
        />
        <RibbonIconButton
          icon={`${R}/numberListIcon.svg`}
          hoverHint={`${R}/numberListHover.svg`}
          label="Number List"
          onClick={actions.numberList}
        />
        <RibbonIconButton
          icon={`${R}/alphaListIcon.svg`}
          hoverHint={`${R}/alphaListHover.svg`}
          label="Alpha List"
          onClick={actions.alphaList}
        />
      </RibbonGroup>

      <RibbonGroup>
        <RibbonIconButton
          icon={`${R}/increaseIndentIcon.svg`}
          hoverHint={`${R}/increaseIndentHover.svg`}
          label="Increase Indent"
          onClick={actions.increaseIndent}
        />
        <RibbonIconButton
          icon={`${R}/decreaseIndentIcon.svg`}
          hoverHint={`${R}/decreaseIndentHover.svg`}
          label="Decrease Indent"
          onClick={actions.decreaseIndent}
        />
      </RibbonGroup>
    </RibbonToolbar>
  )
}
